import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Settings, Loader2, Save, CalendarDays, Clock, Bell, AlertTriangle,
  Plus, Trash2, History, User, PlusCircle, MinusCircle, Calendar,
  CreditCard, Banknote, FileSpreadsheet, Mail, ToggleLeft, Eye, CheckCircle,
  XCircle, ChevronLeft, ChevronRight, Filter, RefreshCw, Users, Search
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";

export default function ParametrosGHPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useState({
    dias_vacaciones_anuales: 15,
    dias_permisos_maximos: 5,
    dias_anticipacion_vacaciones: 7,
    dias_anticipacion_permisos: 2,
    permitir_vacaciones_negativas: false,
    aprobar_automatico_permisos_cortos: false,
    dias_permiso_corto: 1,
    notificar_supervisor: true,
    notificar_rrhh: true,
    notificar_solicitante: true,
    alerta_vacaciones_minimo: 3,
    alerta_permisos_minimo: 2,
    alerta_activa: true
  });

  // Pending vacation days state
  const [pendientes, setPendientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [showPendienteDialog, setShowPendienteDialog] = useState(false);
  const [pendienteForm, setPendienteForm] = useState({
    user_id: "",
    year: new Date().getFullYear() - 1,
    dias_pendientes: 0,
    notas: ""
  });

  // Adjustments state (current year)
  const [ajustes, setAjustes] = useState([]);
  const [showAjusteDialog, setShowAjusteDialog] = useState(false);
  const [ajusteForm, setAjusteForm] = useState({
    user_id: "",
    dias_ajuste: 0,
    motivo: "",
    tipo: "ajuste"
  });

  // Alertas de nómina state
  const [alertasConfig, setAlertasConfig] = useState({
    alertas_activas: true,
    dias_antes_vencimiento_prestamo: 7,
    dias_antes_descuento_avance: 5,
    alertar_prestamos_vencidos: true,
    alertar_avances_pendientes: true,
    alertar_nomina_sin_procesar: true,
    tipo_frecuencia_nomina: "mensual",
    dia_semana_alerta: "viernes",
    dias_mes_alerta: [25],
    notificar_por_email: false,
    notificar_en_dashboard: true,
    emails_destinatarios: [],
    frecuencia_revision: "diaria"
  });
  const [emailInput, setEmailInput] = useState("");
  const [diaInput, setDiaInput] = useState("");

  // Historial de alertas state
  const [historial, setHistorial] = useState([]);
  const [historialStats, setHistorialStats] = useState({ total: 0, nuevas: 0, vistas: 0, atendidas: 0 });
  const [historialPage, setHistorialPage] = useState(1);
  const [historialTotal, setHistorialTotal] = useState(0);
  const [historialLoading, setHistorialLoading] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [showAtenderDialog, setShowAtenderDialog] = useState(false);
  const [alertaSeleccionada, setAlertaSeleccionada] = useState(null);
  const [notasAtencion, setNotasAtencion] = useState("");

  // Asignación masiva de vacaciones state
  const [asignacionForm, setAsignacionForm] = useState({
    year: new Date().getFullYear(),
    dias_totales: 14,
    user_ids: [],
    notas: ""
  });
  const [asignacionLoading, setAsignacionLoading] = useState(false);
  const [selectAllEmpleados, setSelectAllEmpleados] = useState(false);
  const [empleadosFiltrados, setEmpleadosFiltrados] = useState([]);
  const [searchEmpleado, setSearchEmpleado] = useState("");
  const [historialAsignaciones, setHistorialAsignaciones] = useState([]);

  // Días festivos state
  const [diasFestivos, setDiasFestivos] = useState([]);
  const [diaFestivoForm, setDiaFestivoForm] = useState({
    fecha: "",
    nombre: "",
    descripcion: "",
    tipo: "festivo"
  });
  const [showDiaFestivoDialog, setShowDiaFestivoDialog] = useState(false);
  const [diasFestivosLoading, setDiasFestivosLoading] = useState(false);
  const [yearFestivo, setYearFestivo] = useState(new Date().getFullYear());

  // Ajustes de permisos state
  const [ajustesPermisos, setAjustesPermisos] = useState([]);
  const [ajustePermisoForm, setAjustePermisoForm] = useState({
    user_id: "",
    cantidad_ajuste: 0,
    motivo: "",
    tipo: "ajuste"
  });
  const [showAjustePermisoDialog, setShowAjustePermisoDialog] = useState(false);
  const [ajustesPermisosLoading, setAjustesPermisosLoading] = useState(false);
  const [saldoPermisoUsuario, setSaldoPermisoUsuario] = useState(null);

  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [paramsRes, pendientesRes, ajustesRes, usersRes, alertasRes] = await Promise.all([
        axios.get(`${API}/gestion-humana/parametros`, getAuthHeaders()),
        axios.get(`${API}/vacaciones/pendientes`, getAuthHeaders()),
        axios.get(`${API}/vacaciones/ajustes`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/nomina/alertas/config`, getAuthHeaders()).catch(() => ({ data: {} }))
      ]);
      setParams(paramsRes.data);
      setPendientes(pendientesRes.data);
      setAjustes(ajustesRes.data);
      setUsuarios(usersRes.data);
      if (alertasRes.data && Object.keys(alertasRes.data).length > 0) {
        setAlertasConfig(prev => ({ ...prev, ...alertasRes.data }));
      }
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter empleados for asignación masiva
  useEffect(() => {
    const filtered = usuarios.filter(u => {
      const searchLower = searchEmpleado.toLowerCase();
      return (
        (u.name?.toLowerCase().includes(searchLower) || u.email?.toLowerCase().includes(searchLower)) &&
        u.activo !== false // Only active users
      );
    });
    setEmpleadosFiltrados(filtered);
  }, [usuarios, searchEmpleado]);

  // Handle select all toggle for asignación masiva
  useEffect(() => {
    if (selectAllEmpleados) {
      setAsignacionForm(prev => ({
        ...prev,
        user_ids: empleadosFiltrados.map(u => u.id)
      }));
    }
  }, [selectAllEmpleados, empleadosFiltrados]);

  // Fetch historial de asignaciones masivas
  const fetchHistorialAsignaciones = useCallback(async () => {
    setHistorialLoading(true);
    try {
      const response = await axios.get(`${API}/vacaciones/saldos/asignacion-masiva/historial?limit=10`, getAuthHeaders());
      setHistorialAsignaciones(response.data);
    } catch (err) {
      console.error("Error fetching historial:", err);
    } finally {
      setHistorialLoading(false);
    }
  }, [getAuthHeaders]);

  // Load historial when tab changes to asignacion-masiva
  useEffect(() => {
    // Initial load of historial
    fetchHistorialAsignaciones();
  }, [fetchHistorialAsignaciones]);

  // Handle asignación masiva submit
  const handleAsignacionMasiva = async () => {
    if (asignacionForm.user_ids.length === 0) {
      toast.error("Seleccione al menos un empleado");
      return;
    }
    if (asignacionForm.dias_totales <= 0) {
      toast.error("Ingrese una cantidad de días válida");
      return;
    }
    
    setAsignacionLoading(true);
    try {
      const response = await axios.post(`${API}/vacaciones/saldos/asignacion-masiva`, asignacionForm, getAuthHeaders());
      const { total_creados, total_actualizados, total_errores } = response.data;
      
      if (total_errores > 0) {
        toast.warning(`Asignación completada con ${total_errores} errores`);
      } else {
        toast.success(`${total_creados} saldos creados, ${total_actualizados} saldos actualizados`);
      }
      
      // Reset form
      setAsignacionForm({
        year: new Date().getFullYear(),
        dias_totales: 14,
        user_ids: [],
        notas: ""
      });
      setSelectAllEmpleados(false);
      setSearchEmpleado("");
      
      // Reload historial
      fetchHistorialAsignaciones();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al asignar vacaciones");
    } finally {
      setAsignacionLoading(false);
    }
  };

  // Toggle employee selection for asignación masiva
  const toggleEmpleadoSelection = (userId) => {
    setAsignacionForm(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  // ========== DÍAS FESTIVOS ==========
  const fetchDiasFestivos = useCallback(async () => {
    setDiasFestivosLoading(true);
    try {
      const response = await axios.get(`${API}/dias-festivos?year=${yearFestivo}`, getAuthHeaders());
      setDiasFestivos(response.data);
    } catch (err) {
      console.error("Error fetching dias festivos:", err);
    } finally {
      setDiasFestivosLoading(false);
    }
  }, [getAuthHeaders, yearFestivo]);

  useEffect(() => {
    fetchDiasFestivos();
  }, [fetchDiasFestivos]);

  const handleSaveDiaFestivo = async () => {
    if (!diaFestivoForm.fecha || !diaFestivoForm.nombre) {
      toast.error("Complete la fecha y nombre del día festivo");
      return;
    }
    try {
      await axios.post(`${API}/dias-festivos`, diaFestivoForm, getAuthHeaders());
      toast.success("Día festivo agregado");
      setShowDiaFestivoDialog(false);
      setDiaFestivoForm({ fecha: "", nombre: "", descripcion: "", tipo: "festivo" });
      fetchDiasFestivos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar día festivo");
    }
  };

  const handleDeleteDiaFestivo = async (diaId) => {
    if (!confirm("¿Está seguro de eliminar este día festivo?")) return;
    try {
      await axios.delete(`${API}/dias-festivos/${diaId}`, getAuthHeaders());
      toast.success("Día festivo eliminado");
      fetchDiasFestivos();
    } catch (err) {
      toast.error("Error al eliminar día festivo");
    }
  };

  // ========== AJUSTES DE PERMISOS ==========
  const fetchAjustesPermisos = useCallback(async () => {
    setAjustesPermisosLoading(true);
    try {
      const response = await axios.get(`${API}/permisos/ajustes`, getAuthHeaders());
      setAjustesPermisos(response.data);
    } catch (err) {
      console.error("Error fetching ajustes permisos:", err);
    } finally {
      setAjustesPermisosLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchAjustesPermisos();
  }, [fetchAjustesPermisos]);

  const fetchSaldoPermisoUsuario = async (userId) => {
    try {
      const response = await axios.get(`${API}/permisos/saldo-usuario/${userId}`, getAuthHeaders());
      setSaldoPermisoUsuario(response.data);
    } catch (err) {
      console.error("Error fetching saldo permiso:", err);
      setSaldoPermisoUsuario(null);
    }
  };

  const handleSaveAjustePermiso = async () => {
    if (!ajustePermisoForm.user_id || !ajustePermisoForm.motivo) {
      toast.error("Seleccione un usuario y escriba un motivo");
      return;
    }
    if (ajustePermisoForm.cantidad_ajuste === 0) {
      toast.error("La cantidad de ajuste no puede ser 0");
      return;
    }
    try {
      await axios.post(`${API}/permisos/ajustes`, ajustePermisoForm, getAuthHeaders());
      toast.success("Ajuste de permisos guardado");
      setShowAjustePermisoDialog(false);
      setAjustePermisoForm({ user_id: "", cantidad_ajuste: 0, motivo: "", tipo: "ajuste" });
      setSaldoPermisoUsuario(null);
      fetchAjustesPermisos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar ajuste de permisos");
    }
  };

  const handleDeleteAjustePermiso = async (ajusteId) => {
    if (!confirm("¿Está seguro de eliminar este ajuste? Se revertirá el cambio en el saldo del usuario.")) return;
    try {
      await axios.delete(`${API}/permisos/ajustes/${ajusteId}`, getAuthHeaders());
      toast.success("Ajuste eliminado y cambios revertidos");
      fetchAjustesPermisos();
    } catch (err) {
      toast.error("Error al eliminar ajuste");
    }
  };

  // Fetch historial de alertas
  const fetchHistorial = useCallback(async () => {
    try {
      setHistorialLoading(true);
      const params = new URLSearchParams({ page: historialPage, limit: 10 });
      if (filtroTipo) params.append("tipo", filtroTipo);
      if (filtroEstado) params.append("estado", filtroEstado);
      
      const [historialRes, statsRes] = await Promise.all([
        axios.get(`${API}/nomina/alertas/historial?${params}`, getAuthHeaders()),
        axios.get(`${API}/nomina/alertas/historial/stats`, getAuthHeaders())
      ]);
      
      setHistorial(historialRes.data.historial || []);
      setHistorialTotal(historialRes.data.total || 0);
      setHistorialStats(statsRes.data);
    } catch (err) {
      console.error("Error al cargar historial:", err);
    } finally {
      setHistorialLoading(false);
    }
  }, [getAuthHeaders, historialPage, filtroTipo, filtroEstado]);

  // Marcar alerta como vista
  const marcarComoVista = async (alertaId) => {
    try {
      await axios.put(`${API}/nomina/alertas/historial/${alertaId}/ver`, {}, getAuthHeaders());
      toast.success("Alerta marcada como vista");
      fetchHistorial();
    } catch (err) {
      toast.error("Error al marcar alerta");
    }
  };

  // Marcar alerta como atendida
  const marcarComoAtendida = async () => {
    if (!alertaSeleccionada) return;
    try {
      await axios.put(
        `${API}/nomina/alertas/historial/${alertaSeleccionada.id}/atender`,
        { notas: notasAtencion },
        getAuthHeaders()
      );
      toast.success("Alerta marcada como atendida");
      setShowAtenderDialog(false);
      setAlertaSeleccionada(null);
      setNotasAtencion("");
      fetchHistorial();
    } catch (err) {
      toast.error("Error al atender alerta");
    }
  };

  // Eliminar alerta del historial
  const eliminarAlerta = async (alertaId) => {
    if (!window.confirm("¿Eliminar esta alerta del historial?")) return;
    try {
      await axios.delete(`${API}/nomina/alertas/historial/${alertaId}`, getAuthHeaders());
      toast.success("Alerta eliminada");
      fetchHistorial();
    } catch (err) {
      toast.error("Error al eliminar alerta");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/gestion-humana/parametros`, params, getAuthHeaders());
      toast.success("Parámetros guardados exitosamente");
    } catch (err) {
      toast.error("Error al guardar parámetros");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAlertasNomina = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/nomina/alertas/config`, alertasConfig, getAuthHeaders());
      toast.success("Configuración de alertas guardada");
    } catch (err) {
      toast.error("Error al guardar configuración de alertas");
    } finally {
      setSaving(false);
    }
  };

  const handleAlertasChange = (field, value) => {
    setAlertasConfig(prev => ({ ...prev, [field]: value }));
  };

  const addEmail = () => {
    if (emailInput && emailInput.includes("@")) {
      setAlertasConfig(prev => ({
        ...prev,
        emails_destinatarios: [...(prev.emails_destinatarios || []), emailInput]
      }));
      setEmailInput("");
    } else {
      toast.error("Ingrese un email válido");
    }
  };

  const removeEmail = (email) => {
    setAlertasConfig(prev => ({
      ...prev,
      emails_destinatarios: (prev.emails_destinatarios || []).filter(e => e !== email)
    }));
  };

  const addDiaMes = () => {
    const dia = parseInt(diaInput);
    if (dia >= 1 && dia <= 31 && !alertasConfig.dias_mes_alerta.includes(dia)) {
      setAlertasConfig(prev => ({
        ...prev,
        dias_mes_alerta: [...(prev.dias_mes_alerta || []), dia].sort((a, b) => a - b)
      }));
      setDiaInput("");
    } else {
      toast.error("Ingrese un día válido (1-31) que no esté ya en la lista");
    }
  };

  const removeDiaMes = (dia) => {
    setAlertasConfig(prev => ({
      ...prev,
      dias_mes_alerta: (prev.dias_mes_alerta || []).filter(d => d !== dia)
    }));
  };

  const handleChange = (field, value) => {
    setParams(prev => ({ ...prev, [field]: value }));
  };

  const handleSavePendiente = async () => {
    if (!pendienteForm.user_id) {
      toast.error("Seleccione un usuario");
      return;
    }
    if (!Number.isFinite(pendienteForm.dias_pendientes) || pendienteForm.dias_pendientes === 0) {
      toast.error("Ingrese una cantidad de días válida (distinta de cero)");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/vacaciones/pendientes`, pendienteForm, getAuthHeaders());
      toast.success("Días pendientes guardados");
      setShowPendienteDialog(false);
      setPendienteForm({ user_id: "", year: new Date().getFullYear() - 1, dias_pendientes: 0, notas: "" });
      // Refresh pendientes
      const res = await axios.get(`${API}/vacaciones/pendientes`, getAuthHeaders());
      setPendientes(res.data);
    } catch (err) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePendiente = async (id) => {
    if (!window.confirm("¿Eliminar este registro de días pendientes?")) return;
    try {
      await axios.delete(`${API}/vacaciones/pendientes/${id}`, getAuthHeaders());
      toast.success("Registro eliminado");
      const res = await axios.get(`${API}/vacaciones/pendientes`, getAuthHeaders());
      setPendientes(res.data);
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // Ajustes handlers
  const handleSaveAjuste = async () => {
    if (!ajusteForm.user_id) {
      toast.error("Seleccione un usuario");
      return;
    }
    if (ajusteForm.dias_ajuste === 0) {
      toast.error("El ajuste debe ser diferente de 0");
      return;
    }
    if (!ajusteForm.motivo.trim()) {
      toast.error("Ingrese un motivo para el ajuste");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/vacaciones/ajustes`, ajusteForm, getAuthHeaders());
      toast.success(`Ajuste de ${ajusteForm.dias_ajuste > 0 ? '+' : ''}${ajusteForm.dias_ajuste} días aplicado`);
      setShowAjusteDialog(false);
      setAjusteForm({ user_id: "", dias_ajuste: 0, motivo: "", tipo: "ajuste" });
      const res = await axios.get(`${API}/vacaciones/ajustes`, getAuthHeaders());
      setAjustes(res.data);
    } catch (err) {
      toast.error("Error al guardar ajuste");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAjuste = async (id) => {
    if (!window.confirm("¿Eliminar este ajuste? Se revertirán los días en el saldo del usuario.")) return;
    try {
      await axios.delete(`${API}/vacaciones/ajustes/${id}`, getAuthHeaders());
      toast.success("Ajuste eliminado y días revertidos");
      const res = await axios.get(`${API}/vacaciones/ajustes`, getAuthHeaders());
      setAjustes(res.data);
    } catch (err) {
      toast.error("Error al eliminar ajuste");
    }
  };

  // Group ajustes by user
  const ajustesByUser = ajustes.reduce((acc, a) => {
    if (!acc[a.user_id]) {
      acc[a.user_id] = {
        user_name: a.user_name,
        items: [],
        total_ajuste: 0
      };
    }
    acc[a.user_id].items.push(a);
    acc[a.user_id].total_ajuste += a.dias_ajuste;
    return acc;
  }, {});

  // Group pendientes by user
  const pendientesByUser = pendientes.reduce((acc, p) => {
    if (!acc[p.user_id]) {
      acc[p.user_id] = {
        user_name: p.user_name,
        items: []
      };
    }
    acc[p.user_id].items.push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Parámetros de Gestión Humana
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Configure los valores predeterminados para vacaciones, permisos, notificaciones y alertas de nómina
            </p>
          </div>
        </div>

        <Tabs defaultValue="vacaciones" className="space-y-6" onValueChange={(v) => { if (v === "historial-alertas") fetchHistorial(); }}>
          <TabsList className="grid w-full grid-cols-7 max-w-4xl">
            <TabsTrigger value="vacaciones" className="gap-2">
              <CalendarDays className="w-4 h-4" /> Vacaciones
            </TabsTrigger>
            <TabsTrigger value="asignacion-masiva" className="gap-2" data-testid="asignacion-masiva-tab">
              <Users className="w-4 h-4" /> Asignar Días
            </TabsTrigger>
            <TabsTrigger value="dias-festivos" className="gap-2" data-testid="dias-festivos-tab">
              <Calendar className="w-4 h-4" /> Días Festivos
            </TabsTrigger>
            <TabsTrigger value="ajustes-permisos" className="gap-2" data-testid="ajustes-permisos-tab">
              <Clock className="w-4 h-4" /> Ajustes Permisos
            </TabsTrigger>
            <TabsTrigger value="notificaciones" className="gap-2">
              <Bell className="w-4 h-4" /> Notificaciones
            </TabsTrigger>
            <TabsTrigger value="alertas-nomina" className="gap-2">
              <Banknote className="w-4 h-4" /> Alertas Nómina
            </TabsTrigger>
            <TabsTrigger value="historial-alertas" className="gap-2" data-testid="historial-tab">
              <History className="w-4 h-4" /> Historial
            </TabsTrigger>
          </TabsList>

          {/* Tab: Vacaciones y Permisos */}
          <TabsContent value="vacaciones">
            <div className="flex justify-end mb-4">
              <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-params-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Cambios
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vacaciones */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-blue-500" />
                Configuración de Vacaciones
              </CardTitle>
              <CardDescription>
                Parámetros para el módulo de vacaciones
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="dias_vacaciones">Días de vacaciones anuales</Label>
                <Input
                  id="dias_vacaciones"
                  type="number"
                  value={params.dias_vacaciones_anuales}
                  onChange={e => handleChange("dias_vacaciones_anuales", parseInt(e.target.value) || 0)}
                  data-testid="dias-vacaciones-input"
                />
                <p className="text-xs text-slate-500">Días asignados a cada empleado por año</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dias_anticipacion_vac">Días de anticipación mínimos</Label>
                <Input
                  id="dias_anticipacion_vac"
                  type="number"
                  value={params.dias_anticipacion_vacaciones}
                  onChange={e => handleChange("dias_anticipacion_vacaciones", parseInt(e.target.value) || 0)}
                  data-testid="dias-anticipacion-vacaciones-input"
                />
                <p className="text-xs text-slate-500">Días mínimos antes de la fecha de inicio para solicitar</p>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Permitir saldo negativo</Label>
                  <p className="text-xs text-slate-500">Permitir solicitar más días de los disponibles</p>
                </div>
                <Switch
                  checked={params.permitir_vacaciones_negativas}
                  onCheckedChange={v => handleChange("permitir_vacaciones_negativas", v)}
                  data-testid="permitir-negativo-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Permisos */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Configuración de Permisos
              </CardTitle>
              <CardDescription>
                Parámetros para el módulo de permisos laborales
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="dias_permisos">Días de permisos máximos al año</Label>
                <Input
                  id="dias_permisos"
                  type="number"
                  value={params.dias_permisos_maximos}
                  onChange={e => handleChange("dias_permisos_maximos", parseInt(e.target.value) || 0)}
                  data-testid="dias-permisos-input"
                />
                <p className="text-xs text-slate-500">Máximo de días de permiso por empleado al año</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dias_anticipacion_perm">Días de anticipación mínimos</Label>
                <Input
                  id="dias_anticipacion_perm"
                  type="number"
                  value={params.dias_anticipacion_permisos}
                  onChange={e => handleChange("dias_anticipacion_permisos", parseInt(e.target.value) || 0)}
                  data-testid="dias-anticipacion-permisos-input"
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Aprobar automático permisos cortos</Label>
                  <p className="text-xs text-slate-500">Aprobar automáticamente permisos de corta duración</p>
                </div>
                <Switch
                  checked={params.aprobar_automatico_permisos_cortos}
                  onCheckedChange={v => handleChange("aprobar_automatico_permisos_cortos", v)}
                  data-testid="aprobar-auto-switch"
                />
              </div>

              {params.aprobar_automatico_permisos_cortos && (
                <div className="space-y-2">
                  <Label htmlFor="dias_permiso_corto">Días para considerar permiso corto</Label>
                  <Input
                    id="dias_permiso_corto"
                    type="number"
                    value={params.dias_permiso_corto}
                    onChange={e => handleChange("dias_permiso_corto", parseInt(e.target.value) || 1)}
                    data-testid="dias-permiso-corto-input"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Alertas */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Configuración de Alertas
              </CardTitle>
              <CardDescription>
                Configure las alertas cuando los saldos sean bajos
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Alertas activas</Label>
                  <p className="text-xs text-slate-500">Mostrar alertas cuando el saldo sea bajo</p>
                </div>
                <Switch
                  checked={params.alerta_activa}
                  onCheckedChange={v => handleChange("alerta_activa", v)}
                  data-testid="alerta-activa-switch"
                />
              </div>

              {params.alerta_activa && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="alerta_vacaciones">Alerta de vacaciones (días mínimos)</Label>
                    <Input
                      id="alerta_vacaciones"
                      type="number"
                      value={params.alerta_vacaciones_minimo}
                      onChange={e => handleChange("alerta_vacaciones_minimo", parseInt(e.target.value) || 0)}
                      data-testid="alerta-vacaciones-input"
                    />
                    <p className="text-xs text-slate-500">
                      Mostrar alerta cuando queden {params.alerta_vacaciones_minimo} días o menos de vacaciones
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="alerta_permisos">Alerta de permisos (días mínimos)</Label>
                    <Input
                      id="alerta_permisos"
                      type="number"
                      value={params.alerta_permisos_minimo}
                      onChange={e => handleChange("alerta_permisos_minimo", parseInt(e.target.value) || 0)}
                      data-testid="alerta-permisos-input"
                    />
                    <p className="text-xs text-slate-500">
                      Mostrar alerta cuando queden {params.alerta_permisos_minimo} días o menos de permisos
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Notificaciones */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-green-500" />
                Configuración de Notificaciones
              </CardTitle>
              <CardDescription>
                Quién recibe notificaciones por email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div className="space-y-0.5">
                  <Label>Notificar al Supervisor</Label>
                  <p className="text-xs text-slate-500">Cuando se crea una solicitud</p>
                </div>
                <Switch
                  checked={params.notificar_supervisor}
                  onCheckedChange={v => handleChange("notificar_supervisor", v)}
                  data-testid="notificar-supervisor-switch"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div className="space-y-0.5">
                  <Label>Notificar a RRHH</Label>
                  <p className="text-xs text-slate-500">Cuando se aprueba o rechaza</p>
                </div>
                <Switch
                  checked={params.notificar_rrhh}
                  onCheckedChange={v => handleChange("notificar_rrhh", v)}
                  data-testid="notificar-rrhh-switch"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div className="space-y-0.5">
                  <Label>Notificar al Solicitante</Label>
                  <p className="text-xs text-slate-500">Cuando se procesa su solicitud</p>
                </div>
                <Switch
                  checked={params.notificar_solicitante}
                  onCheckedChange={v => handleChange("notificar_solicitante", v)}
                  data-testid="notificar-solicitante-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Vacaciones Pendientes de Años Anteriores - Full Width */}
          <Card className="dark:bg-slate-800 dark:border-slate-700 lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-purple-500" />
                  Vacaciones Pendientes de Años Anteriores
                </CardTitle>
                <CardDescription>
                  Gestione los días de vacaciones acumulados de años anteriores por empleado.
                  El sistema descontará primero los días más antiguos (FIFO).
                </CardDescription>
              </div>
              <Button onClick={() => setShowPendienteDialog(true)} className="gap-2" data-testid="add-pendiente-btn">
                <Plus className="w-4 h-4" /> Ajustar Días Pendientes
              </Button>
            </CardHeader>
            <CardContent>
              {Object.keys(pendientesByUser).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay días de vacaciones pendientes registrados</p>
                  <p className="text-sm mt-1">Use el botón "Agregar Días Pendientes" para registrar días acumulados</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(pendientesByUser).map(([userId, userData]) => (
                    <div key={userId} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                      <div className="bg-slate-100 dark:bg-slate-700/50 px-4 py-2 flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="font-medium">{userData.user_name}</span>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {userData.items.sort((a, b) => a.year - b.year).map(item => (
                            <div
                              key={item.id}
                              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                              data-testid={`pendiente-${item.id}`}
                            >
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="font-mono">{item.year}</Badge>
                                  <span className="text-sm font-medium">
                                    {item.dias_restantes} días restantes
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  Original: {item.dias_pendientes} | Usados: {item.dias_usados || 0}
                                </p>
                                {item.notas && (
                                  <p className="text-xs text-slate-400 mt-1 truncate max-w-[200px]">{item.notas}</p>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeletePendiente(item.id)}
                                data-testid={`delete-pendiente-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>¿Cómo funciona el descuento FIFO?</strong><br/>
                  Cuando se aprueba una solicitud de vacaciones, el sistema descuenta primero los días del año más antiguo 
                  hasta agotarlos, luego continúa con el siguiente año, y finalmente del saldo del año actual.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Ajustes de Vacaciones del Año Actual - Full Width */}
          <Card className="dark:bg-slate-800 dark:border-slate-700 lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-500" />
                  Ajustes de Vacaciones del Año Actual ({new Date().getFullYear()})
                </CardTitle>
                <CardDescription>
                  Añada o descuente días de vacaciones del saldo del año actual para cada empleado.
                  Útil para correcciones, bonificaciones o ajustes especiales.
                </CardDescription>
              </div>
              <Button onClick={() => setShowAjusteDialog(true)} className="gap-2" data-testid="add-ajuste-btn">
                <Plus className="w-4 h-4" /> Nuevo Ajuste
              </Button>
            </CardHeader>
            <CardContent>
              {Object.keys(ajustesByUser).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay ajustes de vacaciones registrados este año</p>
                  <p className="text-sm mt-1">Use el botón "Nuevo Ajuste" para agregar o descontar días</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(ajustesByUser).map(([userId, userData]) => (
                    <div key={userId} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                      <div className="bg-slate-100 dark:bg-slate-700/50 px-4 py-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-500" />
                          <span className="font-medium">{userData.user_name}</span>
                        </div>
                        <Badge className={userData.total_ajuste >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                          Total: {userData.total_ajuste >= 0 ? '+' : ''}{userData.total_ajuste} días
                        </Badge>
                      </div>
                      <div className="p-4">
                        <div className="space-y-2">
                          {userData.items.map(item => (
                            <div
                              key={item.id}
                              className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                              data-testid={`ajuste-${item.id}`}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  {item.dias_ajuste > 0 ? (
                                    <PlusCircle className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <MinusCircle className="w-4 h-4 text-red-500" />
                                  )}
                                  <span className={`text-sm font-medium ${item.dias_ajuste > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {item.dias_ajuste > 0 ? '+' : ''}{item.dias_ajuste} días
                                  </span>
                                  <Badge variant="outline" className="text-xs">{item.tipo}</Badge>
                                </div>
                                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{item.motivo}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Por: {item.created_by_name} • {new Date(item.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteAjuste(item.id)}
                                data-testid={`delete-ajuste-${item.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-700 dark:text-green-300">
                  <strong>¿Cómo funcionan los ajustes?</strong><br/>
                  Los ajustes modifican directamente el saldo total de vacaciones del año actual. 
                  Use valores positivos para añadir días (ej: bonificación) o negativos para descontar (ej: corrección).
                  Al eliminar un ajuste, los días se revierten automáticamente.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
          </TabsContent>

          {/* Tab: Asignación Masiva de Vacaciones */}
          <TabsContent value="asignacion-masiva">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-500" />
                  Asignación Masiva de Días de Vacaciones
                </CardTitle>
                <CardDescription>
                  Asigne días de vacaciones a múltiples empleados de forma masiva para un año específico
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Configuration Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="space-y-2">
                    <Label>Año</Label>
                    <Select
                      value={String(asignacionForm.year)}
                      onValueChange={(v) => setAsignacionForm({ ...asignacionForm, year: parseInt(v) })}
                    >
                      <SelectTrigger data-testid="asignacion-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Días a Asignar</Label>
                    <Input
                      type="number"
                      value={asignacionForm.dias_totales}
                      onChange={(e) => setAsignacionForm({ ...asignacionForm, dias_totales: parseInt(e.target.value) || 0 })}
                      min="1"
                      max="30"
                      data-testid="asignacion-dias-input"
                    />
                    <p className="text-xs text-slate-500">Cantidad de días a asignar</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Notas (opcional)</Label>
                    <Input
                      value={asignacionForm.notas}
                      onChange={(e) => setAsignacionForm({ ...asignacionForm, notas: e.target.value })}
                      placeholder="Ej: Vacaciones anuales 2026"
                      data-testid="asignacion-notas-input"
                    />
                  </div>
                </div>

                {/* Employee Selection */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Seleccionar Empleados</Label>
                    <Badge variant="outline" className="text-indigo-600">
                      {asignacionForm.user_ids.length} de {empleadosFiltrados.length} seleccionados
                    </Badge>
                  </div>
                  
                  {/* Search and Select All */}
                  <div className="flex gap-4 items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={searchEmpleado}
                        onChange={(e) => setSearchEmpleado(e.target.value)}
                        placeholder="Buscar empleado..."
                        className="pl-10"
                        data-testid="asignacion-search-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectAllEmpleados}
                        onCheckedChange={(checked) => {
                          setSelectAllEmpleados(checked);
                          if (!checked) {
                            setAsignacionForm(prev => ({ ...prev, user_ids: [] }));
                          }
                        }}
                        data-testid="asignacion-select-all"
                      />
                      <Label className="cursor-pointer" onClick={() => setSelectAllEmpleados(!selectAllEmpleados)}>
                        Seleccionar todos
                      </Label>
                    </div>
                  </div>

                  {/* Employee List */}
                  <div className="border rounded-lg max-h-80 overflow-y-auto dark:border-slate-600">
                    {empleadosFiltrados.length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                        <p>No se encontraron empleados</p>
                      </div>
                    ) : (
                      <div className="divide-y dark:divide-slate-600">
                        {empleadosFiltrados.map((emp) => (
                          <div 
                            key={emp.id}
                            className={`flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors ${
                              asignacionForm.user_ids.includes(emp.id) ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                            }`}
                            onClick={() => toggleEmpleadoSelection(emp.id)}
                            data-testid={`asignacion-empleado-${emp.id}`}
                          >
                            <Checkbox
                              checked={asignacionForm.user_ids.includes(emp.id)}
                              onCheckedChange={() => toggleEmpleadoSelection(emp.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                              {(emp.name || emp.email || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 dark:text-white truncate">
                                {emp.name || emp.email}
                              </p>
                              <p className="text-xs text-slate-500 truncate">{emp.email}</p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {emp.department_name || "Sin departamento"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4 border-t dark:border-slate-600">
                  <Button
                    onClick={handleAsignacionMasiva}
                    disabled={asignacionLoading || asignacionForm.user_ids.length === 0}
                    className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="asignacion-submit-btn"
                  >
                    {asignacionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CalendarDays className="w-4 h-4" />
                    )}
                    Asignar {asignacionForm.dias_totales} días a {asignacionForm.user_ids.length} empleado(s)
                  </Button>
                </div>

                {/* Info Box */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>¿Cómo funciona?</strong><br/>
                    • Si el empleado no tiene saldo para el año seleccionado, se crea uno nuevo con los días indicados.<br/>
                    • Si ya tiene saldo, los días se <strong>suman</strong> al total existente (útil para ajustes o días adicionales).<br/>
                    • Los días usados no se modifican, solo se incrementa el total disponible.<br/>
                    • Para reducir días, use la sección de "Ajustes de Saldo" en la pestaña Vacaciones.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Historial de Asignaciones Masivas */}
            <Card className="dark:bg-slate-800 dark:border-slate-700 mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-500" />
                  Historial de Asignaciones Masivas
                </CardTitle>
                <CardDescription>
                  Registro de todas las asignaciones masivas realizadas
                </CardDescription>
              </CardHeader>
              <CardContent>
                {historialLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                  </div>
                ) : historialAsignaciones.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No hay asignaciones masivas registradas</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {historialAsignaciones.map((item) => (
                      <div 
                        key={item.id} 
                        className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border dark:border-slate-600"
                        data-testid={`historial-item-${item.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold">
                              {item.year}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900 dark:text-white">
                                {item.dias_asignados} días asignados a {item.total_empleados} empleado(s)
                              </p>
                              <p className="text-xs text-slate-500">
                                {new Date(item.created_at).toLocaleString('es-DO', { 
                                  dateStyle: 'medium', 
                                  timeStyle: 'short' 
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {item.total_creados > 0 && (
                              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                +{item.total_creados} nuevos
                              </Badge>
                            )}
                            {item.total_actualizados > 0 && (
                              <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                {item.total_actualizados} actualizados
                              </Badge>
                            )}
                            {item.total_errores > 0 && (
                              <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                {item.total_errores} errores
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {item.notas && (
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2 italic">
                            "{item.notas}"
                          </p>
                        )}
                        
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <User className="w-3 h-3" />
                          <span>Ejecutado por: {item.ejecutado_por_nombre}</span>
                        </div>
                        
                        {/* Expandable employee list */}
                        {item.empleados_afectados && item.empleados_afectados.length > 0 && (
                          <details className="mt-3">
                            <summary className="cursor-pointer text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                              Ver empleados afectados ({item.empleados_afectados.length})
                            </summary>
                            <div className="mt-2 pl-4 border-l-2 border-indigo-200 dark:border-indigo-700 space-y-1">
                              {item.empleados_afectados.slice(0, 10).map((emp, idx) => (
                                <p key={idx} className="text-xs text-slate-600 dark:text-slate-400">
                                  • {emp.user_name}
                                  {emp.dias_anteriores !== undefined && (
                                    <span className="text-slate-400 ml-1">
                                      ({emp.dias_anteriores} → {emp.nuevo_total} días)
                                    </span>
                                  )}
                                  {emp.dias_totales !== undefined && emp.dias_anteriores === undefined && (
                                    <span className="text-green-600 dark:text-green-400 ml-1">
                                      (nuevo: {emp.dias_totales} días)
                                    </span>
                                  )}
                                </p>
                              ))}
                              {item.empleados_afectados.length > 10 && (
                                <p className="text-xs text-slate-400 italic">
                                  ... y {item.empleados_afectados.length - 10} más
                                </p>
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Notificaciones */}
          <TabsContent value="notificaciones">
            <div className="flex justify-end mb-4">
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Cambios
              </Button>
            </div>
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />
                  Configuración de Notificaciones
                </CardTitle>
                <CardDescription>
                  Configure quién recibe notificaciones cuando se crean o modifican solicitudes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Notificar al Supervisor</Label>
                    <p className="text-xs text-slate-500">El supervisor directo recibe las solicitudes</p>
                  </div>
                  <Switch
                    checked={params.notificar_supervisor}
                    onCheckedChange={v => handleChange("notificar_supervisor", v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Notificar a RRHH</Label>
                    <p className="text-xs text-slate-500">El departamento de RRHH recibe copia de las solicitudes</p>
                  </div>
                  <Switch
                    checked={params.notificar_rrhh}
                    onCheckedChange={v => handleChange("notificar_rrhh", v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Notificar al Solicitante</Label>
                    <p className="text-xs text-slate-500">El empleado recibe confirmación de sus solicitudes</p>
                  </div>
                  <Switch
                    checked={params.notificar_solicitante}
                    onCheckedChange={v => handleChange("notificar_solicitante", v)}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Alertas de Nómina */}
          <TabsContent value="alertas-nomina">
            <div className="flex justify-end mb-4">
              <Button onClick={handleSaveAlertasNomina} disabled={saving} className="gap-2" data-testid="save-alertas-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar Configuración de Alertas
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Configuración General de Alertas */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-blue-500" />
                    Configuración General
                  </CardTitle>
                  <CardDescription>
                    Active o desactive las alertas de nómina
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <div>
                      <Label className="text-base font-medium">Sistema de Alertas</Label>
                      <p className="text-xs text-slate-500">Activar/desactivar todas las alertas</p>
                    </div>
                    <Switch
                      checked={alertasConfig.alertas_activas}
                      onCheckedChange={v => handleAlertasChange("alertas_activas", v)}
                      data-testid="alertas-activas-switch"
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Alertar préstamos vencidos/atrasados</Label>
                        <p className="text-xs text-slate-500">Notificar cuando hay pagos atrasados</p>
                      </div>
                      <Switch
                        checked={alertasConfig.alertar_prestamos_vencidos}
                        onCheckedChange={v => handleAlertasChange("alertar_prestamos_vencidos", v)}
                        disabled={!alertasConfig.alertas_activas}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Alertar avances pendientes</Label>
                        <p className="text-xs text-slate-500">Notificar avances sin descontar</p>
                      </div>
                      <Switch
                        checked={alertasConfig.alertar_avances_pendientes}
                        onCheckedChange={v => handleAlertasChange("alertar_avances_pendientes", v)}
                        disabled={!alertasConfig.alertas_activas}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Alertar nómina sin procesar</Label>
                        <p className="text-xs text-slate-500">Notificar cuando no se ha procesado la nómina del mes</p>
                      </div>
                      <Switch
                        checked={alertasConfig.alertar_nomina_sin_procesar}
                        onCheckedChange={v => handleAlertasChange("alertar_nomina_sin_procesar", v)}
                        disabled={!alertasConfig.alertas_activas}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Parámetros de Tiempo */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-green-500" />
                    Parámetros de Tiempo
                  </CardTitle>
                  <CardDescription>
                    Configure los tiempos para generar alertas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Días antes de vencimiento de préstamo</Label>
                    <Input
                      type="number"
                      value={alertasConfig.dias_antes_vencimiento_prestamo}
                      onChange={e => handleAlertasChange("dias_antes_vencimiento_prestamo", parseInt(e.target.value) || 7)}
                      min={1}
                      max={30}
                      disabled={!alertasConfig.alertas_activas}
                    />
                    <p className="text-xs text-slate-500">Alertar cuando quedan X días para terminar el préstamo</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Días antes de descuento de avance</Label>
                    <Input
                      type="number"
                      value={alertasConfig.dias_antes_descuento_avance}
                      onChange={e => handleAlertasChange("dias_antes_descuento_avance", parseInt(e.target.value) || 5)}
                      min={1}
                      max={15}
                      disabled={!alertasConfig.alertas_activas}
                    />
                    <p className="text-xs text-slate-500">Alertar X días antes del descuento del avance</p>
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Configuración de Alerta por Nómina sin Procesar
                    </h4>
                    
                    <div className="space-y-2">
                      <Label>Frecuencia de la nómina</Label>
                      <Select
                        value={alertasConfig.tipo_frecuencia_nomina}
                        onValueChange={v => handleAlertasChange("tipo_frecuencia_nomina", v)}
                        disabled={!alertasConfig.alertas_activas}
                      >
                        <SelectTrigger data-testid="frecuencia-nomina-select">
                          <SelectValue placeholder="Seleccione frecuencia" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="semanal">Semanal</SelectItem>
                          <SelectItem value="quincenal">Quincenal</SelectItem>
                          <SelectItem value="mensual">Mensual</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Seleccione la frecuencia de pago de su nómina para configurar las alertas
                      </p>
                    </div>

                    {/* Semanal: Selector de día de la semana */}
                    {alertasConfig.tipo_frecuencia_nomina === "semanal" && (
                      <div className="space-y-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <Label>Día de la semana para alertar</Label>
                        <Select
                          value={alertasConfig.dia_semana_alerta}
                          onValueChange={v => handleAlertasChange("dia_semana_alerta", v)}
                          disabled={!alertasConfig.alertas_activas}
                        >
                          <SelectTrigger data-testid="dia-semana-select">
                            <SelectValue placeholder="Seleccione día" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="lunes">Lunes</SelectItem>
                            <SelectItem value="martes">Martes</SelectItem>
                            <SelectItem value="miercoles">Miércoles</SelectItem>
                            <SelectItem value="jueves">Jueves</SelectItem>
                            <SelectItem value="viernes">Viernes</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          Se alertará cada {alertasConfig.dia_semana_alerta || "viernes"} si la nómina semanal no ha sido procesada
                        </p>
                      </div>
                    )}

                    {/* Quincenal: Selector de dos días del mes */}
                    {alertasConfig.tipo_frecuencia_nomina === "quincenal" && (
                      <div className="space-y-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <Label>Días del mes para alertar (quincena)</Label>
                        <p className="text-xs text-slate-500 mb-2">
                          Configure los días del mes cuando se debe verificar la nómina quincenal (típicamente 15 y 30)
                        </p>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="number"
                            value={diaInput}
                            onChange={e => setDiaInput(e.target.value)}
                            placeholder="Día (1-31)"
                            min={1}
                            max={31}
                            className="w-32"
                            disabled={!alertasConfig.alertas_activas}
                            data-testid="dia-mes-input"
                          />
                          <Button 
                            onClick={addDiaMes} 
                            variant="outline" 
                            size="sm"
                            disabled={!alertasConfig.alertas_activas}
                            data-testid="add-dia-mes-btn"
                          >
                            <Plus className="w-4 h-4 mr-1" /> Agregar
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(alertasConfig.dias_mes_alerta || []).map((dia) => (
                            <Badge key={dia} variant="secondary" className="gap-1 pr-1 bg-amber-100 dark:bg-amber-800">
                              Día {dia}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 ml-1 hover:bg-amber-200 dark:hover:bg-amber-700"
                                onClick={() => removeDiaMes(dia)}
                                disabled={!alertasConfig.alertas_activas}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                        {(alertasConfig.dias_mes_alerta || []).length > 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400">
                            Se alertará los días {(alertasConfig.dias_mes_alerta || []).join(" y ")} de cada mes
                          </p>
                        )}
                      </div>
                    )}

                    {/* Mensual: Selector de un día del mes */}
                    {alertasConfig.tipo_frecuencia_nomina === "mensual" && (
                      <div className="space-y-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <Label>Días del mes para alertar</Label>
                        <p className="text-xs text-slate-500 mb-2">
                          Configure el día (o días) del mes cuando se debe verificar la nómina mensual
                        </p>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="number"
                            value={diaInput}
                            onChange={e => setDiaInput(e.target.value)}
                            placeholder="Día (1-31)"
                            min={1}
                            max={31}
                            className="w-32"
                            disabled={!alertasConfig.alertas_activas}
                            data-testid="dia-mes-mensual-input"
                          />
                          <Button 
                            onClick={addDiaMes} 
                            variant="outline" 
                            size="sm"
                            disabled={!alertasConfig.alertas_activas}
                            data-testid="add-dia-mes-mensual-btn"
                          >
                            <Plus className="w-4 h-4 mr-1" /> Agregar
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(alertasConfig.dias_mes_alerta || []).map((dia) => (
                            <Badge key={dia} variant="secondary" className="gap-1 pr-1 bg-green-100 dark:bg-green-800">
                              Día {dia}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 ml-1 hover:bg-green-200 dark:hover:bg-green-700"
                                onClick={() => removeDiaMes(dia)}
                                disabled={!alertasConfig.alertas_activas}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                        {(alertasConfig.dias_mes_alerta || []).length > 0 && (
                          <p className="text-xs text-green-600 dark:text-green-400">
                            Se alertará el día {(alertasConfig.dias_mes_alerta || []).join(" o ")} de cada mes si la nómina no ha sido procesada
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-2">
                    <Label>Frecuencia de revisión del sistema</Label>
                    <Select
                      value={alertasConfig.frecuencia_revision}
                      onValueChange={v => handleAlertasChange("frecuencia_revision", v)}
                      disabled={!alertasConfig.alertas_activas}
                    >
                      <SelectTrigger data-testid="frecuencia-revision-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="diaria">Diaria</SelectItem>
                        <SelectItem value="semanal">Semanal</SelectItem>
                        <SelectItem value="quincenal">Quincenal</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Con qué frecuencia el sistema verifica las condiciones de alerta</p>
                  </div>
                </CardContent>
              </Card>

              {/* Notificaciones de Alertas */}
              <Card className="dark:bg-slate-800 dark:border-slate-700 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-purple-500" />
                    Canal de Notificaciones
                  </CardTitle>
                  <CardDescription>
                    Configure cómo desea recibir las alertas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-center justify-between p-4 border dark:border-slate-700 rounded-lg">
                      <div>
                        <Label className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4" />
                          Mostrar en Dashboard
                        </Label>
                        <p className="text-xs text-slate-500">Las alertas aparecerán en el dashboard de nómina</p>
                      </div>
                      <Switch
                        checked={alertasConfig.notificar_en_dashboard}
                        onCheckedChange={v => handleAlertasChange("notificar_en_dashboard", v)}
                        disabled={!alertasConfig.alertas_activas}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border dark:border-slate-700 rounded-lg">
                      <div>
                        <Label className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Notificar por Email
                        </Label>
                        <p className="text-xs text-slate-500">Enviar alertas por correo electrónico</p>
                      </div>
                      <Switch
                        checked={alertasConfig.notificar_por_email}
                        onCheckedChange={v => handleAlertasChange("notificar_por_email", v)}
                        disabled={!alertasConfig.alertas_activas}
                      />
                    </div>
                  </div>

                  {alertasConfig.notificar_por_email && (
                    <div className="space-y-3">
                      <Label>Emails destinatarios</Label>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          value={emailInput}
                          onChange={e => setEmailInput(e.target.value)}
                          placeholder="email@ejemplo.com"
                          className="flex-1"
                        />
                        <Button onClick={addEmail} variant="outline">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(alertasConfig.emails_destinatarios || []).map((email, idx) => (
                          <Badge key={idx} variant="secondary" className="gap-1 pr-1">
                            {email}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-4 w-4 p-0 ml-1"
                              onClick={() => removeEmail(email)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>💡 Tip:</strong> Las alertas se generan automáticamente según la frecuencia configurada. 
                      Puede ver todas las alertas activas en el Dashboard de Nómina.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Historial de Alertas */}
          <TabsContent value="historial-alertas">
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                        <History className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{historialStats.total}</p>
                        <p className="text-xs text-slate-500">Total Alertas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{historialStats.nuevas}</p>
                        <p className="text-xs text-slate-500">Nuevas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg">
                        <Eye className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{historialStats.vistas}</p>
                        <p className="text-xs text-slate-500">Vistas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{historialStats.atendidas}</p>
                        <p className="text-xs text-slate-500">Atendidas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Filters and Table */}
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5 text-purple-500" />
                      Historial de Alertas
                    </CardTitle>
                    <CardDescription>
                      Registro de todas las alertas generadas por el sistema
                    </CardDescription>
                  </div>
                  <Button onClick={fetchHistorial} variant="outline" size="sm" className="gap-2" data-testid="refresh-historial-btn">
                    <RefreshCw className={`w-4 h-4 ${historialLoading ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                </CardHeader>
                <CardContent>
                  {/* Filters */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-slate-500" />
                      <Select value={filtroTipo} onValueChange={v => { setFiltroTipo(v === "_all" ? "" : v); setHistorialPage(1); }}>
                        <SelectTrigger className="w-48" data-testid="filtro-tipo-select">
                          <SelectValue placeholder="Filtrar por tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">Todos los tipos</SelectItem>
                          <SelectItem value="prestamo_por_finalizar">Préstamo por finalizar</SelectItem>
                          <SelectItem value="prestamo_atrasado">Préstamo atrasado</SelectItem>
                          <SelectItem value="avance_pendiente">Avance pendiente</SelectItem>
                          <SelectItem value="nomina_sin_procesar">Nómina sin procesar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Select value={filtroEstado} onValueChange={v => { setFiltroEstado(v === "_all" ? "" : v); setHistorialPage(1); }}>
                        <SelectTrigger className="w-40" data-testid="filtro-estado-select">
                          <SelectValue placeholder="Estado" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_all">Todos</SelectItem>
                          <SelectItem value="nueva">Nuevas</SelectItem>
                          <SelectItem value="vista">Vistas</SelectItem>
                          <SelectItem value="atendida">Atendidas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Table */}
                  {historialLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : historial.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hay alertas en el historial</p>
                      <p className="text-sm mt-1">Las alertas generadas aparecerán aquí</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {historial.map(alerta => (
                          <div
                            key={alerta.id}
                            className={`p-4 rounded-lg border ${
                              alerta.estado === "nueva" ? "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800" :
                              alerta.estado === "vista" ? "bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800" :
                              "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800"
                            }`}
                            data-testid={`alerta-${alerta.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={
                                    alerta.prioridad === "alta" ? "bg-red-500" :
                                    alerta.prioridad === "media" ? "bg-amber-500" : "bg-slate-500"
                                  }>
                                    {alerta.prioridad}
                                  </Badge>
                                  <Badge variant="outline">
                                    {alerta.tipo?.replace(/_/g, " ")}
                                  </Badge>
                                  <Badge variant={
                                    alerta.estado === "nueva" ? "destructive" :
                                    alerta.estado === "vista" ? "secondary" : "default"
                                  }>
                                    {alerta.estado}
                                  </Badge>
                                </div>
                                <h4 className="font-medium text-slate-900 dark:text-white">{alerta.titulo}</h4>
                                <p className="text-sm text-slate-600 dark:text-slate-400">{alerta.mensaje}</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                  <span>Generada: {new Date(alerta.fecha_generada).toLocaleString()}</span>
                                  {alerta.atendida && (
                                    <span className="text-green-600">
                                      Atendida por: {alerta.atendido_por_nombre} ({new Date(alerta.atendida_at).toLocaleDateString()})
                                    </span>
                                  )}
                                </div>
                                {alerta.notas_atencion && (
                                  <p className="mt-2 text-sm italic text-slate-500 bg-white dark:bg-slate-800 p-2 rounded">
                                    "{alerta.notas_atencion}"
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2 ml-4">
                                {alerta.estado === "nueva" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => marcarComoVista(alerta.id)}
                                    className="gap-1"
                                    data-testid={`ver-alerta-${alerta.id}`}
                                  >
                                    <Eye className="w-4 h-4" /> Vista
                                  </Button>
                                )}
                                {alerta.estado !== "atendida" && (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => { setAlertaSeleccionada(alerta); setShowAtenderDialog(true); }}
                                    className="gap-1 bg-green-600 hover:bg-green-700"
                                    data-testid={`atender-alerta-${alerta.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4" /> Atender
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => eliminarAlerta(alerta.id)}
                                  className="text-red-500 hover:text-red-700"
                                  data-testid={`eliminar-alerta-${alerta.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      {historialTotal > 10 && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-slate-700">
                          <p className="text-sm text-slate-500">
                            Mostrando {((historialPage - 1) * 10) + 1} - {Math.min(historialPage * 10, historialTotal)} de {historialTotal}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={historialPage === 1}
                              onClick={() => setHistorialPage(p => p - 1)}
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={historialPage * 10 >= historialTotal}
                              onClick={() => setHistorialPage(p => p + 1)}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Días Festivos */}
          <TabsContent value="dias-festivos">
            <div className="space-y-6">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-red-500" />
                      Calendario de Días Festivos
                    </CardTitle>
                    <CardDescription>
                      Configure los días festivos y no laborables para el cálculo correcto de vacaciones y permisos
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={yearFestivo.toString()} onValueChange={(v) => setYearFestivo(parseInt(v))}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027, 2028].map(y => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={() => setShowDiaFestivoDialog(true)} className="gap-2">
                      <Plus className="w-4 h-4" /> Agregar Día Festivo
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {diasFestivosLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : diasFestivos.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-400" />
                      <p>No hay días festivos configurados para {yearFestivo}</p>
                      <p className="text-sm mt-1">Los días sábados y domingos ya son excluidos automáticamente</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {diasFestivos.map(dia => (
                        <div key={dia.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className="text-center bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg p-2 min-w-[60px]">
                              <div className="text-xs font-medium">
                                {new Date(dia.fecha + 'T12:00:00').toLocaleDateString('es-DO', { weekday: 'short' })}
                              </div>
                              <div className="text-lg font-bold">
                                {new Date(dia.fecha + 'T12:00:00').getDate()}
                              </div>
                              <div className="text-xs">
                                {new Date(dia.fecha + 'T12:00:00').toLocaleDateString('es-DO', { month: 'short' })}
                              </div>
                            </div>
                            <div>
                              <p className="font-medium">{dia.nombre}</p>
                              {dia.descripcion && (
                                <p className="text-sm text-slate-500">{dia.descripcion}</p>
                              )}
                              <Badge variant="outline" className="mt-1">
                                {dia.tipo === 'festivo' ? 'Día Festivo' : dia.tipo === 'feriado' ? 'Feriado' : 'Especial'}
                              </Badge>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-100"
                            onClick={() => handleDeleteDiaFestivo(dia.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Ajustes de Permisos */}
          <TabsContent value="ajustes-permisos">
            <div className="space-y-6">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      Ajustes de Permisos del Año Actual ({new Date().getFullYear()})
                    </CardTitle>
                    <CardDescription>
                      Ajuste la cantidad de permisos de los usuarios para cuadrar con saldos anteriores
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowAjustePermisoDialog(true)} className="gap-2">
                    <Plus className="w-4 h-4" /> Nuevo Ajuste
                  </Button>
                </CardHeader>
                <CardContent>
                  {ajustesPermisosLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                    </div>
                  ) : ajustesPermisos.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Clock className="w-12 h-12 mx-auto mb-2 text-slate-400" />
                      <p>No hay ajustes de permisos registrados</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {ajustesPermisos.map(ajuste => (
                        <div key={ajuste.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${ajuste.cantidad_ajuste > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {ajuste.cantidad_ajuste > 0 ? <PlusCircle className="w-5 h-5" /> : <MinusCircle className="w-5 h-5" />}
                            </div>
                            <div>
                              <p className="font-medium">{ajuste.user_name}</p>
                              <p className="text-sm text-slate-500">{ajuste.motivo}</p>
                              <p className="text-xs text-slate-400">
                                Por {ajuste.created_by_name} • {new Date(ajuste.created_at).toLocaleDateString('es-DO')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <Badge variant={ajuste.cantidad_ajuste > 0 ? "default" : "destructive"}>
                                {ajuste.cantidad_ajuste > 0 ? '+' : ''}{ajuste.cantidad_ajuste} permisos
                              </Badge>
                              <p className="text-xs text-slate-500 mt-1">
                                {ajuste.cantidad_anterior} → {ajuste.cantidad_nueva}
                              </p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-100"
                              onClick={() => handleDeleteAjustePermiso(ajuste.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Dialog para agregar días pendientes */}
        <Dialog open={showPendienteDialog} onOpenChange={setShowPendienteDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Agregar/Ajustar Días de Vacaciones Pendientes</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Usuario</Label>
                <Select
                  value={pendienteForm.user_id}
                  onValueChange={v => setPendienteForm({ ...pendienteForm, user_id: v })}
                >
                  <SelectTrigger data-testid="pendiente-user-select">
                    <SelectValue placeholder="Seleccionar usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Año</Label>
                  <Input
                    type="number"
                    value={pendienteForm.year}
                    onChange={e => setPendienteForm({ ...pendienteForm, year: parseInt(e.target.value) })}
                    max={new Date().getFullYear() - 1}
                    data-testid="pendiente-year-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Año del que provienen los días</p>
                </div>
                <div>
                  <Label>Días a Ajustar</Label>
                  <Input
                    type="number"
                    value={pendienteForm.dias_pendientes}
                    onChange={e => setPendienteForm({ ...pendienteForm, dias_pendientes: parseInt(e.target.value) || 0 })}
                    data-testid="pendiente-dias-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Positivo = añadir, Negativo = quitar</p>
                </div>
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea
                  value={pendienteForm.notas}
                  onChange={e => setPendienteForm({ ...pendienteForm, notas: e.target.value })}
                  placeholder="Motivo o comentarios adicionales"
                  data-testid="pendiente-notas-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPendienteDialog(false)}>Cancelar</Button>
              <Button onClick={handleSavePendiente} disabled={saving} data-testid="save-pendiente-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para agregar ajuste */}
        <Dialog open={showAjusteDialog} onOpenChange={setShowAjusteDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Nuevo Ajuste de Vacaciones</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Usuario</Label>
                <Select
                  value={ajusteForm.user_id}
                  onValueChange={v => setAjusteForm({ ...ajusteForm, user_id: v })}
                >
                  <SelectTrigger data-testid="ajuste-user-select">
                    <SelectValue placeholder="Seleccionar usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Días a Ajustar</Label>
                  <Input
                    type="number"
                    value={ajusteForm.dias_ajuste}
                    onChange={e => setAjusteForm({ ...ajusteForm, dias_ajuste: parseInt(e.target.value) || 0 })}
                    data-testid="ajuste-dias-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Positivo = añadir, Negativo = descontar</p>
                </div>
                <div>
                  <Label>Tipo de Ajuste</Label>
                  <Select
                    value={ajusteForm.tipo}
                    onValueChange={v => setAjusteForm({ ...ajusteForm, tipo: v })}
                  >
                    <SelectTrigger data-testid="ajuste-tipo-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ajuste">Ajuste General</SelectItem>
                      <SelectItem value="correccion">Corrección</SelectItem>
                      <SelectItem value="bonificacion">Bonificación</SelectItem>
                      <SelectItem value="descuento">Descuento</SelectItem>
                      <SelectItem value="antiguedad">Por Antigüedad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Motivo *</Label>
                <Textarea
                  value={ajusteForm.motivo}
                  onChange={e => setAjusteForm({ ...ajusteForm, motivo: e.target.value })}
                  placeholder="Explique el motivo del ajuste"
                  data-testid="ajuste-motivo-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAjusteDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveAjuste} disabled={saving} data-testid="save-ajuste-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Aplicar Ajuste
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para atender alerta */}
        <Dialog open={showAtenderDialog} onOpenChange={setShowAtenderDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Atender Alerta
              </DialogTitle>
            </DialogHeader>
            {alertaSeleccionada && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={
                      alertaSeleccionada.prioridad === "alta" ? "bg-red-500" :
                      alertaSeleccionada.prioridad === "media" ? "bg-amber-500" : "bg-slate-500"
                    }>
                      {alertaSeleccionada.prioridad}
                    </Badge>
                    <Badge variant="outline">
                      {alertaSeleccionada.tipo?.replace(/_/g, " ")}
                    </Badge>
                  </div>
                  <h4 className="font-medium">{alertaSeleccionada.titulo}</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{alertaSeleccionada.mensaje}</p>
                </div>
                <div>
                  <Label>Notas de atención (opcional)</Label>
                  <Textarea
                    value={notasAtencion}
                    onChange={e => setNotasAtencion(e.target.value)}
                    placeholder="Describa las acciones tomadas para atender esta alerta..."
                    rows={3}
                    data-testid="notas-atencion-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAtenderDialog(false); setAlertaSeleccionada(null); setNotasAtencion(""); }}>
                Cancelar
              </Button>
              <Button onClick={marcarComoAtendida} className="bg-green-600 hover:bg-green-700 gap-2" data-testid="confirmar-atender-btn">
                <CheckCircle className="w-4 h-4" />
                Marcar como Atendida
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para agregar día festivo */}
        <Dialog open={showDiaFestivoDialog} onOpenChange={setShowDiaFestivoDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-red-500" />
                Agregar Día Festivo
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={diaFestivoForm.fecha}
                  onChange={(e) => setDiaFestivoForm({ ...diaFestivoForm, fecha: e.target.value })}
                  className="dark:bg-slate-900"
                />
              </div>
              <div>
                <Label>Nombre del día festivo *</Label>
                <Input
                  value={diaFestivoForm.nombre}
                  onChange={(e) => setDiaFestivoForm({ ...diaFestivoForm, nombre: e.target.value })}
                  placeholder="Ej: Día de la Independencia"
                  className="dark:bg-slate-900"
                />
              </div>
              <div>
                <Label>Descripción (opcional)</Label>
                <Textarea
                  value={diaFestivoForm.descripcion}
                  onChange={(e) => setDiaFestivoForm({ ...diaFestivoForm, descripcion: e.target.value })}
                  placeholder="Descripción adicional..."
                  className="dark:bg-slate-900"
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={diaFestivoForm.tipo} onValueChange={(v) => setDiaFestivoForm({ ...diaFestivoForm, tipo: v })}>
                  <SelectTrigger className="dark:bg-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="festivo">Día Festivo</SelectItem>
                    <SelectItem value="feriado">Feriado Nacional</SelectItem>
                    <SelectItem value="especial">Día Especial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDiaFestivoDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveDiaFestivo} className="gap-2">
                <Save className="w-4 h-4" />
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog para ajuste de permisos */}
        <Dialog open={showAjustePermisoDialog} onOpenChange={(open) => {
          setShowAjustePermisoDialog(open);
          if (!open) {
            setAjustePermisoForm({ user_id: "", cantidad_ajuste: 0, motivo: "", tipo: "ajuste" });
            setSaldoPermisoUsuario(null);
          }
        }}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Ajustar Cantidad de Permisos
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Usuario *</Label>
                <Select 
                  value={ajustePermisoForm.user_id} 
                  onValueChange={(v) => {
                    setAjustePermisoForm({ ...ajustePermisoForm, user_id: v });
                    fetchSaldoPermisoUsuario(v);
                  }}
                >
                  <SelectTrigger className="dark:bg-slate-900">
                    <SelectValue placeholder="Seleccionar usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.filter(u => u.is_active).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {saldoPermisoUsuario && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Saldo actual:</strong> {saldoPermisoUsuario.cantidad_disponible} permisos disponibles
                    ({saldoPermisoUsuario.cantidad_total} total - {saldoPermisoUsuario.cantidad_usada} usados)
                  </p>
                </div>
              )}
              
              <div>
                <Label>Cantidad de ajuste *</Label>
                <div className="flex items-center gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAjustePermisoForm({ ...ajustePermisoForm, cantidad_ajuste: ajustePermisoForm.cantidad_ajuste - 1 })}
                  >
                    <MinusCircle className="w-4 h-4" />
                  </Button>
                  <Input
                    type="number"
                    value={ajustePermisoForm.cantidad_ajuste}
                    onChange={(e) => setAjustePermisoForm({ ...ajustePermisoForm, cantidad_ajuste: parseInt(e.target.value) || 0 })}
                    className="w-24 text-center dark:bg-slate-900"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAjustePermisoForm({ ...ajustePermisoForm, cantidad_ajuste: ajustePermisoForm.cantidad_ajuste + 1 })}
                  >
                    <PlusCircle className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Use números negativos para reducir y positivos para aumentar
                </p>
              </div>
              
              <div>
                <Label>Motivo del ajuste *</Label>
                <Textarea
                  value={ajustePermisoForm.motivo}
                  onChange={(e) => setAjustePermisoForm({ ...ajustePermisoForm, motivo: e.target.value })}
                  placeholder="Ej: Ajuste por permisos tomados antes de usar la plataforma"
                  className="dark:bg-slate-900"
                />
              </div>
              
              <div>
                <Label>Tipo de ajuste</Label>
                <Select value={ajustePermisoForm.tipo} onValueChange={(v) => setAjustePermisoForm({ ...ajustePermisoForm, tipo: v })}>
                  <SelectTrigger className="dark:bg-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ajuste">Ajuste Regular</SelectItem>
                    <SelectItem value="correccion">Corrección</SelectItem>
                    <SelectItem value="bonus">Bonificación</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAjustePermisoDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveAjustePermiso} className="gap-2">
                <Save className="w-4 h-4" />
                Guardar Ajuste
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
